/**
 * Pay-first booking helpers (L12, L13, L18). Pure functions so the booking
 * state machine's edges are unit-testable without Apollo or Stripe.
 */

/** Mirrors startups3 `ADVISOR_SESSION_BOOKING_SOURCES` (`payFirst.js`). */
export const BOOKING_SOURCES = ['link', 'link-waive', 'directory', 'profile', 'landing'] as const
export type BookingSource = (typeof BOOKING_SOURCES)[number]

export const isBookingSource = (value: unknown): value is BookingSource =>
  typeof value === 'string' && (BOOKING_SOURCES as readonly string[]).includes(value)

/**
 * Where the booking form is rendered:
 * - `member-link`: the public `/members/[slug]/book` share link
 * - `advisors`: the in-app Advisors book page (`/advisors/sessions/new`)
 */
export type BookingEntry = 'member-link' | 'advisors'

/**
 * L18: `?ref` → `bookingSource`.
 * - `?ref=directory|landing|profile` wins on every entry point.
 * - The share link defaults to `link-waive` with a waive code, `link` without.
 * - In-app booking without a known ref stays unattributed (null).
 * - `?ref=link` on the in-app page follows the same waive rule as the share link,
 *   because the guest redirect from a share link lands there.
 */
export const resolveBookingSource = ({
  ref,
  waiveCode,
  entry,
}: {
  ref?: string | null
  waiveCode?: string | null
  entry: BookingEntry
}): BookingSource | null => {
  const normalized = String(ref || '')
    .trim()
    .toLowerCase()
  const hasWaive = !!String(waiveCode || '').trim()
  if (normalized === 'directory' || normalized === 'landing' || normalized === 'profile') {
    return normalized
  }
  if (entry === 'member-link' || normalized === 'link' || normalized === 'link-waive') {
    return hasWaive ? 'link-waive' : 'link'
  }
  return null
}

// ---------------------------------------------------------------------------
// Draft (L12): kept in sessionStorage, keyed by expert, cleared after a request.
// ---------------------------------------------------------------------------

export const BOOK_DRAFT_VERSION = 1
const DRAFT_PREFIX = 'sco.advisors.bookDraft.'

export type SessionBookDraft = {
  v: typeof BOOK_DRAFT_VERSION
  minutes: number
  reason: string
  /** ISO strings, as sent to `requestAdvisorSession`. */
  times: string[]
  waiveCode: string
  ref: string
  /** The guest reached the register/login step; resume to payment once signed in. */
  awaitingAuth?: boolean
  savedAt: string
}

export const bookDraftKey = (expertUserId: string) => `${DRAFT_PREFIX}${expertUserId}`

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const defaultStorage = (): DraftStorage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

/** Parses a stored draft; anything malformed or from another version is ignored. */
export const parseBookDraft = (raw: string | null | undefined): SessionBookDraft | null => {
  if (!raw) {
    return null
  }
  try {
    const value = JSON.parse(raw)
    if (!value || typeof value !== 'object' || value.v !== BOOK_DRAFT_VERSION) {
      return null
    }
    const minutes = Number(value.minutes)
    return {
      v: BOOK_DRAFT_VERSION,
      minutes: Number.isFinite(minutes) ? minutes : NaN,
      reason: typeof value.reason === 'string' ? value.reason : '',
      times: Array.isArray(value.times)
        ? value.times.filter((t: unknown) => typeof t === 'string' && t).slice(0, 3)
        : [],
      waiveCode: typeof value.waiveCode === 'string' ? value.waiveCode : '',
      ref: typeof value.ref === 'string' ? value.ref : '',
      awaitingAuth: value.awaitingAuth === true,
      savedAt: typeof value.savedAt === 'string' ? value.savedAt : '',
    }
  } catch {
    return null
  }
}

export const readBookDraft = (
  expertUserId: string,
  storage: DraftStorage | null = defaultStorage()
): SessionBookDraft | null => {
  if (!expertUserId || !storage) {
    return null
  }
  try {
    return parseBookDraft(storage.getItem(bookDraftKey(expertUserId)))
  } catch {
    return null
  }
}

export const writeBookDraft = (
  expertUserId: string,
  draft: Omit<SessionBookDraft, 'v' | 'savedAt'>,
  storage: DraftStorage | null = defaultStorage(),
  now: Date = new Date()
): void => {
  if (!expertUserId || !storage) {
    return
  }
  try {
    const value: SessionBookDraft = { ...draft, v: BOOK_DRAFT_VERSION, savedAt: now.toISOString() }
    storage.setItem(bookDraftKey(expertUserId), JSON.stringify(value))
  } catch {
    // Storage full or blocked: the form still works, the draft just won't survive a reload.
  }
}

export const clearBookDraft = (expertUserId: string, storage: DraftStorage | null = defaultStorage()) => {
  if (!expertUserId || !storage) {
    return
  }
  try {
    storage.removeItem(bookDraftKey(expertUserId))
  } catch {
    // ignore
  }
}

/** URL wins for waive/ref (a fresh share link); the draft fills them in after a signup redirect drops them. */
export const mergeDraftWithUrl = (
  draft: SessionBookDraft | null,
  url: { waiveCode?: string | null; ref?: string | null }
): { waiveCode: string; ref: string } => ({
  waiveCode: String(url.waiveCode || '').trim() || draft?.waiveCode || '',
  ref: String(url.ref || '').trim() || draft?.ref || '',
})

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type BookingErrorKind =
  /** Pair already has an open session: link to it instead of an error. */
  | 'already-active'
  /** Card step not finished — let the seeker finish/retry the card. */
  | 'card-not-authorized'
  /** Hold doesn't match the booking (it was released) — prepare a fresh one. */
  | 're-prepare'
  /** This PaymentIntent already booked a session. */
  | 'already-used'
  /** Waive code no longer valid (e.g. rotated) — offer to continue at the standard rate. */
  | 'waive-invalid'
  /** Bad input — back to the details step. */
  | 'input'
  /** Anything else: show the message; retry is allowed. */
  | 'other'

export type BookingError = { kind: BookingErrorKind; code: string | null; message: string }

const BOOKING_CODES = [
  'ADVISOR_SESSION_ALREADY_ACTIVE',
  'ADVISOR_SESSION_PAYMENT_NOT_AUTHORIZED',
  'ADVISOR_SESSION_PAYMENT_MISMATCH',
  'ADVISOR_SESSION_PAYMENT_ALREADY_USED',
  'ADVISOR_SESSION_PRECHARGE_REQUIRED',
  'ADVISOR_SESSION_PRECHARGE_FAILED',
  'ADVISOR_SESSION_INPUT_INVALID',
  'ADVISOR_SESSION_EXPERT_UNAVAILABLE',
  'ADVISOR_SESSION_CONNECT_REQUIRED',
  'ADVISOR_SESSION_TIME_NOT_PROPOSED',
  'USER_NOT_AUTHENTICATED',
] as const

export const errorText = (error: unknown): string => {
  if (typeof error === 'string') {
    return error
  }
  if (error && typeof error === 'object') {
    const record = error as {
      graphQLErrors?: Array<{ message?: string }>
      errors?: Array<{ message?: string }>
      message?: string
    }
    return record.errors?.[0]?.message || record.graphQLErrors?.[0]?.message || record.message || ''
  }
  return ''
}

/** First known error code in the message, or null. */
export const bookingErrorCode = (error: unknown): string | null => {
  const text = errorText(error)
  return BOOKING_CODES.find(code => text.includes(code)) || null
}

/**
 * Maps a prepare/request failure to the next step and founder-facing copy.
 * `hasWaiveCode` changes what INPUT_INVALID most likely means.
 */
export const classifyBookingError = (
  error: unknown,
  { hasWaiveCode = false }: { hasWaiveCode?: boolean } = {}
): BookingError => {
  const code = bookingErrorCode(error)
  switch (code) {
    case 'ADVISOR_SESSION_ALREADY_ACTIVE':
      return {
        kind: 'already-active',
        code,
        message:
          'You already have an open session with this advisor. Finish or cancel it before booking another. Any card hold from this attempt was released.',
      }
    case 'ADVISOR_SESSION_PAYMENT_NOT_AUTHORIZED':
      return {
        kind: 'card-not-authorized',
        code,
        message: 'Your card has not been authorized yet. Finish the card step, then try again.',
      }
    case 'ADVISOR_SESSION_PAYMENT_MISMATCH':
      return {
        kind: 're-prepare',
        code,
        message:
          'The booking changed after you entered your card, so we released that hold. Authorize your card again for the current amount.',
      }
    case 'ADVISOR_SESSION_PRECHARGE_REQUIRED':
      return {
        kind: 're-prepare',
        code,
        message: 'This session needs a card hold before we can send the request. Authorize your card below.',
      }
    case 'ADVISOR_SESSION_PRECHARGE_FAILED':
      return {
        kind: 're-prepare',
        code,
        message: 'We could not place a hold on that card. Try again or use a different card.',
      }
    case 'ADVISOR_SESSION_PAYMENT_ALREADY_USED':
      return {
        kind: 'already-used',
        code,
        message:
          'That payment was already used for a booking. Check My sessions before booking again — you were not charged twice.',
      }
    case 'ADVISOR_SESSION_INPUT_INVALID':
      return hasWaiveCode
        ? {
            kind: 'waive-invalid',
            code,
            message:
              'This complimentary link is no longer valid — the advisor may have changed the code. You can book at the standard rate or ask them for a new link.',
          }
        : {
            kind: 'input',
            code,
            message: 'Check the session length, topic, and suggested times, then try again.',
          }
    case 'ADVISOR_SESSION_EXPERT_UNAVAILABLE':
      return { kind: 'other', code, message: 'This advisor is not accepting paid sessions right now.' }
    case 'ADVISOR_SESSION_CONNECT_REQUIRED':
      return {
        kind: 'other',
        code,
        message: 'This advisor has not finished payment setup yet, so paid sessions are unavailable.',
      }
    case 'USER_NOT_AUTHENTICATED':
      return { kind: 'other', code, message: 'Sign in to book this session.' }
    default: {
      const raw = errorText(error)
        .replace(/^(ApolloError|CombinedGraphQLErrors|GraphQL error|Error):\s*/i, '')
        .trim()
      return { kind: 'other', code: null, message: raw || 'Could not book this session. Try again.' }
    }
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const REASON_MAX = 140

export type BookDetailsInput = {
  minutes: number
  reason: string
  times: string[]
  acceptsSessions: boolean
}

/** Returns founder-facing copy for the first problem, or null when the details step is complete. */
export const validateBookDetails = (
  input: BookDetailsInput,
  isValidMinutes: (minutes: number) => boolean
): string | null => {
  if (!input.acceptsSessions) {
    return 'Choose an advisor who is taking sessions.'
  }
  if (!isValidMinutes(input.minutes)) {
    return 'Choose a session length between 1 and 180 minutes.'
  }
  if (!input.reason.trim()) {
    return 'Add a topic so the advisor knows what you want to cover.'
  }
  if (!input.times.length) {
    return 'Pick at least one suggested time.'
  }
  if (input.times.length > 3) {
    return 'Suggest up to three times.'
  }
  return null
}
